import { useAppDispatch, useAppSelector } from '@app';
//~ Import the selectors from your slice file
import { SkipOrArgs } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    selectIsCollectionSelected,
    selectSelectedCollectionId,
    setSelectedCollectionId,
} from '../slices/collectionEntitySlice';
import { Collection, DEFAULT_COLLECTION } from '../types';
import { useCollectionById, UseCollectionByIdArgs } from './useCollectionById';

//~ You can also define individual hooks for each selector here

export const useSelectedCollectionId = () => {
    const storeId = useAppSelector(selectSelectedCollectionId);
    const { collectionId: urlId } = useParams<{ collectionId: string }>();
    return urlId || storeId || null;
};

export const useIsCollectionSelected = () => {
    return useAppSelector(selectIsCollectionSelected);
};

/**
 * Custom hook to access selectors for the collectionEntity slice.
 *
 * @returns An object containing selector functions bound to useSelector.
 */
export const useSelectedCollection = () => {
    //~ Define selector functions for relevant properties here

    const isCollectionSelected = useIsCollectionSelected();
    const selectedCollectionId = useSelectedCollectionId();

    const queryArgs: SkipOrArgs<UseCollectionByIdArgs> = useMemo(() => {
        if (!selectedCollectionId) return skipToken;
        return { collectionId: selectedCollectionId };
    }, [selectedCollectionId]);

    const { data, ...queryResult } = useCollectionById(undefined, queryArgs);

    const selectedCollection: Collection = useMemo(() => {
        return mergeWithDefault(data, DEFAULT_COLLECTION);
    }, [data]);

    return {
        //~ Return all the defined selector values here
        isCollectionSelected,
        selectedCollectionId,
        selectedCollection,
        ...queryResult,
    };
};

export const useSelectCollection = () => {
    const dispatch = useAppDispatch();
    const selectCollection = useCallback(
        (collectionId: Collection['id']) => {
            dispatch(setSelectedCollectionId(collectionId));
        },
        [dispatch],
    );

    return { selectCollection };
};
